import { useEffect, useMemo, useState } from 'react';

import Button from 'epn-ui/umd/dist/components/Button';
import Checkbox from 'epn-ui/umd/dist/components/CheckboxInput';
import Form from 'epn-ui/umd/dist/components/Form';
import Input from 'epn-ui/umd/dist/components/InputText';
import Select from 'epn-ui/umd/dist/components/Select';
import TreeSelect from 'epn-ui/umd/dist/components/TreeSelect';
import throttle from 'lodash/throttle';

import useI18n from '@i18n';

import styles from '@pages/tools/feeds/FeedForm/index.module.scss';

import * as TYPES from '@api/types';
import apiHooks, { ICreateUseRequestInfiniteResponse, ICreateUseRequestResponse } from '@apiHooks';
import { CurrencyTypes } from 'src/types.d';

import BaseSemanticLink from '@base/BaseSemanticLink';

import routes from '@config/routes.json';

import { convertParamsToString } from '@utils/router';

const {
    useOffers,
    usePlacements,
    useProductDumps,
    useCategories,
    useDeeplinks,
    useDomains,
} = apiHooks;

export interface ICurrency {
    key: CurrencyTypes;
    value: CurrencyTypes;
}

export interface IFormat {
    value: string;
}

export interface IFormData {
    feedName: string;
    offer: (TYPES.IGetOffersDataFormatted & { label: string; value: number }) | null;
    placement: { label: string; value: string } | null;
    dump: (TYPES.IGetDumpDataFormatted & { label: string; value: number }) | null;
    category: (string | number)[];
    currency?: ICurrency | null;
    format: IFormat;
    deeplink?: (TYPES.IGetDeeplinksDataFormatted & { label: string; value: string }) | null;
    subid?: string;
    hasDomain?: boolean;
    domain?: TYPES.IGetDomainsDataFormatted | null;
}

const formats: IFormat[] = [{ value: 'yml' }, { value: 'csv' }];

const DEFAULT_VALUES: IFormData = {
    feedName: '',
    offer: null,
    placement: null,
    dump: null,
    category: [],
    currency: null,
    format: formats[0],
    deeplink: null,
    subid: '',
    hasDomain: false,
    domain: null,
};

export interface IFeedFormProps {
    onSubmit: (data: IFormData) => void;
    defaultValues?: IFormData;
    linkBack?: string;
    isEditable?: boolean;
}

const FeedForm: React.FC<IFeedFormProps> = ({
    onSubmit,
    defaultValues,
    linkBack,
    isEditable = false,
}) => {
    const { t, locale } = useI18n('Feeds');
    const { t: tCommon } = useI18n('Common');
    const [offerBySearch] = useState<string>('');
    const [hasDomain, setHasDomain] = useState(false);
    const [form] = Form.useForm<IFormData>();
    const { t: tErrors } = useI18n('Errors');

    const offerId = Form.useWatch('offer', form);
    const dumpId = Form.useWatch('dump', form);

    const {
        data: offers = [],
        isLoading: isOffersLoading,
        nextPage: nextOffersPage,
        isReachingEnd: isReachingEndOffers,
        error: isOffersError,
    } = useOffers({
        params: {
            limit: 20,
            lang: locale,
            viewRules: 'role_user',
            hasFeed: true,
            fields: 'currency,title',
            search: offerBySearch,
        },
        dependencies: [offerBySearch, locale],
    }) as ICreateUseRequestInfiniteResponse<TYPES.IGetOffersDataFormatted[]>;

    const offer = useMemo(() => {
        if (isEditable && defaultValues?.offer) {
            return defaultValues.offer;
        }
        return offers.find(search => +search.id === +offerId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [offerId, offers, defaultValues]);

    const {
        data: placements = [],
        isLoading: isPlacementsLoading,
        nextPage: nextPlacementsPage,
        isReachingEnd: isReachingEndPlacements,
        error: isPlacementsError,
    } = usePlacements({
        condition: !!offer,
        params: {
            statuses: 'allow',
            fields: 'id,url',
            limit: 20,
            offerIds: offer?.id,
        },
        dependencies: [offer?.id],
    }) as ICreateUseRequestInfiniteResponse<TYPES.IGetPlacementsDataFormatted[]>;

    const { data: dumps = [], isLoading: isDumpsLoading } = useProductDumps({
        condition: !!offer && !isEditable,
        params: {
            lang: locale,
        },
        urlParams: {
            offerId: offer?.id,
        },
        dependencies: [offer?.id],
    }) as ICreateUseRequestResponse<TYPES.IGetDumpsDataFormatted[]>;

    const dump = useMemo(() => {
        return dumps.find(search => +search.id === +dumpId);
    }, [dumpId, dumps]);

    const {
        data: categories = [],
        isLoading: isCategoriesLoading,
        error: isCategoriesError,
    } = useCategories({
        condition: !!dump,
        urlParams: {
            dumpId: dump?.id,
        },
        dependencies: [dump?.id],
    }) as ICreateUseRequestResponse<TYPES.IGetCategoriesDataFormatted[]>;

    let currencies: ICurrency[] = [];

    if (offer && offer.currency) {
        currencies = offer.currency.map(currency => ({
            key: currency,
            value: currency,
        }));

        if (!offer.currency.includes(CurrencyTypes.RUB)) {
            currencies.push({
                key: CurrencyTypes.RUB,
                value: CurrencyTypes.RUB,
            });
        }
    }

    const {
        data: deeplinks = [],
        isLoading: isDeeplinksLoading,
        nextPage: nextDeeplinksPage,
        isReachingEnd: isReachingEndDeeplinks,
        error: isDeeplinksError,
    } = useDeeplinks({
        condition: !!offer?.id,
        params: {
            offerId: offer?.id,
            statuses: ['new', 'working'],
            fields: 'description,hash',
            limit: 20,
        },
        dependencies: [offer?.id],
    }) as ICreateUseRequestInfiniteResponse<TYPES.IGetDeeplinksDataFormatted[]>;

    const { data: domains = [], isLoading: isDomainsLoading, error: isDomainsError } = useDomains({
        condition: hasDomain,
        params: {
            status: 'active',
            type: 'redirect',
        },
        dependencies: [hasDomain],
    }) as ICreateUseRequestResponse<TYPES.IGetDomainsDataFormatted[] | null>;

    // на данный момент хуки usePlacements, useDeeplinks, useDomains всегда возвращают пустой массив, независимо от того,был ли запрос на сервер
    const isEmptyPlacements = placements?.length === 0 && !!offer;
    const isEmptyDeeplinks = deeplinks?.length === 0 && !!offer;
    const isEmptyDomains = domains?.length === 0 && !!offer;

    const onScrollBottom = throttle(({ target }, handler) => {
        const isScrolledToBottom =
            target.scrollHeight - Math.floor(target.scrollTop) === target.clientHeight;

        if (isScrolledToBottom) {
            handler();
        }
    }, 100);

    const SelectFilter = (inputValue, option) =>
        option.title.toLowerCase().includes(inputValue.toLowerCase());

    const onFinish = data => {
        onSubmit({
            ...data,
            feedName: data.feedName.trim(),
            offer,
            dump,
        });
    };

    useEffect(() => {
        const correctDefaultValue = defaultValues
            ? { ...defaultValues, offer: defaultValues?.offer }
            : DEFAULT_VALUES;
        form.setFieldsValue(correctDefaultValue);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [defaultValues]);

    return (
        <Form className={`mt-8 mb-28 ${styles.feedForm}`} onFinish={onFinish} form={form}>
            <Form.Item
                rules={[
                    { required: true, message: tCommon('Required field') },
                    {
                        max: 100,
                        message: t('Maximum length is n symbols', { length: 100 }),
                    },
                ]}
                name="feedName"
                label={t('Name of the product feed')}
                required
            >
                <Input
                    disabled={isEditable}
                    placeholder={t('For ex., The Most Popular Goods on AliExpress')}
                />
            </Form.Item>
            <Form.Item
                rules={[{ required: true, message: tCommon('Required field') }]}
                name="offer"
                label={tCommon('Choose offer')}
                required
            >
                <Select
                    disabled={isEditable}
                    showSearch
                    placeholder={t('Start typing the name of the offer')}
                    loading={isOffersLoading}
                    options={offers}
                    fieldNames={{
                        label: 'title',
                        value: 'id',
                    }}
                    filterOption={SelectFilter}
                    onPopupScroll={e =>
                        !isOffersLoading &&
                        !isReachingEndOffers &&
                        onScrollBottom(e, nextOffersPage)
                    }
                    notFoundContent={tCommon('Nothing found')}
                />
            </Form.Item>
            {isOffersError && (
                <span className="text-red-500 text-p3">
                    {tErrors('Something went wrong')}.&nbsp;
                    {t('Try again or contact technical support')}.
                </span>
            )}
            <Form.Item
                rules={[{ required: true, message: tCommon('Required field') }]}
                name="placement"
                label={t('Choose a traffic source')}
                required
            >
                <Select
                    disabled={!offer || isEmptyPlacements || isEditable}
                    placeholder={t('Choose a traffic source below')}
                    loading={isPlacementsLoading}
                    options={placements}
                    fieldNames={{
                        value: 'id',
                        label: 'url',
                    }}
                    onPopupScroll={e =>
                        !isPlacementsLoading &&
                        !isReachingEndPlacements &&
                        onScrollBottom(e, nextPlacementsPage)
                    }
                    notFoundContent={tCommon('Nothing found')}
                />
            </Form.Item>
            {isPlacementsError && (
                <span className="text-red-500 text-p3">
                    {tErrors('Something went wrong')}.&nbsp;
                    {t('Try again or contact technical support')}.
                </span>
            )}
            {!isEditable && isEmptyPlacements && !isPlacementsLoading && (
                <div className="mb-2 -mt-4 text-p3">
                    <span className="text-blueGray-500">{t('You have no sites yet')}.</span>
                    &nbsp;
                    <BaseSemanticLink
                        className="text-red-400 border-red-400 transition border-b-[1px] hover:text-red-500 hover:border-red-500"
                        to={routes.placements.list}
                    >
                        {t('Add')}?
                    </BaseSemanticLink>
                </div>
            )}
            <Form.Item
                rules={[{ required: true, message: tCommon('Required field') }]}
                name="dump"
                label={t('Product feed')}
                required
            >
                <Select
                    disabled={!offer || isEditable}
                    placeholder={t('Choose one of the options')}
                    loading={isDumpsLoading}
                    options={dumps}
                    fieldNames={{ value: 'id', label: 'name' }}
                    notFoundContent={tCommon('Nothing found')}
                />
            </Form.Item>
            <Form.Item name="category" label={t('Category')}>
                {!dump || isEditable ? (
                    <Input
                        disabled
                        placeholder={tCommon('not chosen')}
                        value={categories.map(category => category.name).join(', ')}
                    />
                ) : (
                    <TreeSelect
                        placeholder={
                            !dump || isEditable ? t('not chosen') : t('Choose one or more options')
                        }
                        loading={isCategoriesLoading}
                        treeData={categories}
                        fieldNames={{ label: 'name', value: 'id' }}
                        notFoundContent={tCommon('Nothing found')}
                        multiple
                    />
                )}
            </Form.Item>
            {isCategoriesError && (
                <span className="text-red-500 text-p3">
                    {tErrors('Something went wrong')}.&nbsp;
                    {t('Try again or contact technical support')}.
                </span>
            )}
            <Form.Item
                rules={[{ required: true, message: t('Choose one of the options') }]}
                name="currency"
                label={t('Currency')}
                required
            >
                <Select
                    disabled={!offer}
                    placeholder={t('Choose one of the options')}
                    options={currencies}
                    notFoundContent={tCommon('Nothing found')}
                />
            </Form.Item>
            <Form.Item
                rules={[{ required: true, message: t('Choose one of the options') }]}
                name="format"
                label={t('Feed format')}
                required
            >
                <Select
                    placeholder={t('Choose feed format')}
                    options={formats}
                    notFoundContent={tCommon('Nothing found')}
                />
            </Form.Item>
            <Form.Item
                rules={[{ required: true, message: t('Choose deeplink') }]}
                name="deeplink"
                label={t('DeepLink')}
                required
            >
                <Select
                    disabled={(!offer || isEmptyDeeplinks) && !isEditable}
                    placeholder={
                        (!offer || isEmptyDeeplinks) && !isEditable
                            ? tCommon('not chosen')
                            : t('Choose one of the options')
                    }
                    loading={isDeeplinksLoading}
                    options={deeplinks}
                    fieldNames={{ label: 'description', value: 'hash' }}
                    onPopupScroll={e =>
                        !isDeeplinksLoading &&
                        !isReachingEndDeeplinks &&
                        onScrollBottom(e, nextDeeplinksPage)
                    }
                    notFoundContent={tCommon('Nothing found')}
                />
            </Form.Item>
            {isDeeplinksError && (
                <span className="text-red-500 text-p3">
                    {tErrors('Something went wrong')}.&nbsp;
                    {t('Try again or contact technical support')}.
                </span>
            )}
            {isEmptyDeeplinks && !isDeeplinksLoading && (
                <div className="mb-2 -mt-4 text-p3">
                    <span className="text-blueGray-500">{t('You have no deeplinks yet')}.</span>
                    &nbsp;
                    <BaseSemanticLink
                        className="text-red-400 border-red-400 transition border-b-[1px] hover:text-red-500 hover:border-red-500"
                        to={`${routes.tools.affiliateLinks}?${convertParamsToString({
                            offerId: offer?.id,
                            dumpId: dump?.id,
                        })}`}
                    >
                        {t('Create now')}.
                    </BaseSemanticLink>
                </div>
            )}
            <Form.Item
                name="subid"
                label="Subid"
                rules={[
                    {
                        pattern: /^[a-zA-Z0-9_-]{0,50}$/,
                        message: t('Sub may only contain the following symbols'),
                    },
                ]}
            >
                <Input placeholder={t('Enter subid here')} />
            </Form.Item>
            {(form.getFieldValue('offer')?.id
                ? form.getFieldValue('offer').id !== 1
                : form.getFieldValue('offer') !== 1) && (
                <div className="mb-6">
                    <Checkbox
                        label={t('Use a parked domain')}
                        onChange={e => setHasDomain(e.target.checked)}
                    />
                </div>
            )}
            {hasDomain && form.getFieldValue('offer') !== 1 && (
                <>
                    <Form.Item name="domain" label={t('Domain')}>
                        <Select
                            placeholder={t('Choose one of the options')}
                            loading={isDomainsLoading}
                            options={domains}
                            fieldNames={{ value: 'domain' }}
                            notFoundContent={tCommon('Nothing found')}
                        />
                    </Form.Item>

                    {isDomainsError && (
                        <span className="text-red-500 text-p3">
                            {tErrors('Something went wrong')}.&nbsp;
                            {t('Try again or contact technical support')}.
                        </span>
                    )}
                    {isEmptyDomains && !isDomainsLoading && (
                        <div className="mt-1 text-p3">
                            <span className="text-blueGray-500">
                                {t('You have no parking domains yet')}
                            </span>
                            &nbsp;
                            <BaseSemanticLink
                                className="text-red-400 border-red-400 transition border-b-[1px] hover:text-red-500 hover:border-red-500"
                                to={routes.tools.domains}
                            >
                                {t('Add')}?
                            </BaseSemanticLink>
                        </div>
                    )}
                </>
            )}
            <div className="flex flex-col-reverse justify-end mt-8 xs:flex-row gap-[16px]">
                {linkBack && (
                    <Button type="default" href={linkBack}>
                        {tCommon('Back')}
                    </Button>
                )}
                <Button htmlType="submit" size="middle" type="primary" className="w-full xs:w-auto">
                    {isEditable ? t('Update feed') : t('Create feed')}
                </Button>
            </div>
        </Form>
    );
};

export default FeedForm;
